import { URLS } from "@core/shared/utils";
import { cn } from "@design-system/lib/utils";
import { Button } from "@design-system/components/Button";
import { useIsLoggedIn } from "@framework/account/hooks";
import { authEvents } from "@framework/auth/events";
import { trans, plainTrans } from "@mongez/localization";
import { useNavigate } from "@remix-run/react";
import { Key } from "lucide-react";
import { unlockCodePortal } from "../../atoms/unlock-code.atom";

type UnlockCodeButtonProps = {
  variant?: "default" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  className?: string;
  /** Icon-only below xl (short label from xl up) with a tooltip/aria-label. */
  responsive?: boolean;
};

export function UnlockCodeButton({
  variant = "outline",
  size = "md",
  className,
  responsive = false,
}: UnlockCodeButtonProps) {
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();

  const handleClick = () => {
    if (!isLoggedIn) {
      navigate(URLS.auth.login);
      const event = authEvents.onLogin(() => {
        setTimeout(() => {
          unlockCodePortal.open();

          event.unsubscribe();
        }, 1_500);
      });

      return;
    }

    unlockCodePortal.open();
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleClick}
      className={cn(responsive && "whitespace-nowrap px-2.5 xl:px-3", className)}
      title={plainTrans("unlockCode.enterActivationCode")}
      aria-label={plainTrans("unlockCode.enterActivationCode")}
    >
      <Key className={cn("h-4 w-4", responsive ? "xl:me-2" : "me-2")} />
      {responsive ? (
        <span className="hidden xl:inline">
          {trans("unlockCode.activationCodeShort")}
        </span>
      ) : (
        trans("unlockCode.enterActivationCode")
      )}
    </Button>
  );
}
